import { FunctionComponent, useEffect, useState } from "react";
import { FilteredByFlightDatesMedia, getMediaFilteredByFlightDates } from "../../../api/channelsApi";
import { Guid } from "../../../api/baseApi";
import { formatDate } from "../../../services/dateService";
import { Button } from "../../components/Button";

export type FlightDatesViewMode = "normal" | "selectingFlightDates" | "filteredByFlightDates";

interface FlightDatesFilterProps {
    channelId: Guid;
    nestedPlaylistId: Guid;
    viewMode: FlightDatesViewMode;
    onViewModeChange: (viewMode: FlightDatesViewMode) => void;
    onFilterStarted: () => void;
    onFilterApplied: (items: FilteredByFlightDatesMedia[]) => void;
    onClear: () => void;
    setIsLoading: (isLoading: boolean) => void;
}

const FlightDatesFilter: FunctionComponent<FlightDatesFilterProps> = (props) => {
    const [flightDatesFrom, setFlightDatesFrom] = useState("");
    const [flightDatesTo, setFlightDatesTo] = useState("");
    const [flightDatesFilterError, setFlightDatesFilterError] = useState("");

    useEffect(() => {
        setFlightDatesFrom("");
        setFlightDatesTo("");
        setFlightDatesFilterError("");
    }, [props.nestedPlaylistId]);

    const toggleFilter = () => {
        let nextViewMode: FlightDatesViewMode = props.viewMode === "selectingFlightDates"
            ? "normal"
            : "selectingFlightDates";
        props.onViewModeChange(nextViewMode);
    }

    const applyFilter = async () => {
        if(!flightDatesFrom || !flightDatesTo){
            setFlightDatesFilterError("From and To dates are required");
            return;
        }

        if(new Date(flightDatesFrom) > new Date(flightDatesTo)){
            setFlightDatesFilterError("From date must be before or equal to To date");
            return;
        }

        setFlightDatesFilterError("");
        props.onFilterStarted();
        props.setIsLoading(true);
        let response = await getMediaFilteredByFlightDates(props.channelId, props.nestedPlaylistId, flightDatesFrom, flightDatesTo);
        if(response.type === "succes"){
            props.onFilterApplied(response.data.items);
        }
        props.setIsLoading(false);
    }

    const clearFilter = () => {
        setFlightDatesFrom("");
        setFlightDatesTo("");
        setFlightDatesFilterError("");
        props.onClear();
    }

    return <div className="flight-dates-filter">
        <Button
            color="green"
            label={`Flight Dates Filter ${props.viewMode === "selectingFlightDates" ? "▲" : "▼"}`}
            onClick={toggleFilter}
        />
        {props.viewMode === "filteredByFlightDates" && <div className="flight-dates-filter__selected">
            <span>{formatDate(flightDatesFrom)} - {formatDate(flightDatesTo)}</span>
            <button type="button" aria-label="Clear flight dates filter" onClick={clearFilter}>
                <span aria-hidden="true">&times;</span>
            </button>
        </div>}
        {props.viewMode === "selectingFlightDates" && <div className="flight-dates-filter__popup">
            <b>Filter by Flight Dates</b>
            <label htmlFor="flight-dates-from">From</label>
            <input
                id="flight-dates-from"
                className="form-control"
                type="date"
                value={flightDatesFrom}
                onChange={event => {setFlightDatesFrom(event.target.value); setFlightDatesFilterError("")}}
            />
            <label htmlFor="flight-dates-to">To</label>
            <input
                id="flight-dates-to"
                className="form-control"
                type="date"
                value={flightDatesTo}
                onChange={event => {setFlightDatesTo(event.target.value); setFlightDatesFilterError("")}}
            />
            <p className="flight-dates-filter__hint">Shows playlist items whose flight-date range overlaps the dates above.</p>
            {flightDatesFilterError && <span className="flight-dates-filter__error">{flightDatesFilterError}</span>}
            <div className="flight-dates-filter__actions">
                <Button color="green" label="Apply" onClick={applyFilter} />
            </div>
        </div>}
    </div>;
}

export default FlightDatesFilter;
